import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Save, Play, Plus, Trash2, ChevronDown, ChevronUp,
  Mail, MessageSquare, MessageCircle, Globe, Edit, CheckSquare,
  UserPlus, Bell, Zap, Settings, HelpCircle, Copy, X, GripVertical,
  RefreshCw, CheckCircle, AlertCircle, Check, Search, Loader2
} from 'lucide-react';
import { workflowsV2API, templatesAPI, whatsappAPI } from '../services/api';
import {
  TRIGGER_EVENTS,
  ACTION_TYPES,
  CONDITION_OPERATORS,
  CONDITION_FIELDS,
  TEMPLATE_VARIABLES,
  WORKFLOW_STATUSES,
  SCHEDULE_TYPES,
  DELAY_UNITS,
  RECURRING_FREQUENCIES,
  getEmptyWorkflow,
  getEmptyAction,
  getEmptyConditionRule,
  getEmptyConditionGroup
} from '../constants/workflowOptions';

// Phase 16.3 — CRM-field options exposed in the workflow Send WhatsApp
// action's variable-mapping UI. Each option pastes a `{{lead.X}}` token
// into the variable input; the workflow engine resolves these tokens at
// runtime against the triggered lead document (with "N/A" fallback for
// empty fields). Keep these in sync with build_context() in
// services/workflow_engine.py.
const CRM_FIELD_OPTIONS = [
  { label: 'Owner Name', value: '{{lead.owner_name}}' },
  { label: 'Property Name', value: '{{lead.property_name}}' },
  { label: 'Package / Plan', value: '{{lead.package_name}}' },
  { label: 'Selected OTAs', value: '{{lead.selected_otas}}' },
  { label: 'Onboarding Days', value: '{{lead.onboarding_days}}' },
  { label: 'Setup Amount', value: '{{lead.setup_amount}}' },
  { label: 'Commission %', value: '{{lead.commission_percentage}}' },
  { label: 'Token Paid', value: '{{lead.token_paid}}' },
  { label: 'Pending Setup Amount', value: '{{lead.pending_setup}}' },
  // Commercials (CRM Create Account → Commercial Details). Bare tokens
  // resolve at the context root in services/workflow_engine.py build_context().
  { label: 'Finalize Setup Token', value: '{{finalize_setup_token}}' },
  { label: 'Token Advance', value: '{{token_advance}}' },
  { label: 'Pending Payment', value: '{{pending_payment}}' },
  { label: 'Interested Plan', value: '{{interested_plan}}' },
  { label: 'Setup Payment Status', value: '{{setup_payment_status}}' },
  { label: 'Agreed Commission', value: '{{commission_agreed}}' },
  { label: 'OTA Listing Link', value: '{{lead.ota_listing_link}}' },
  { label: 'Pending Documents', value: '{{lead.pending_documents}}' },
  // Common extras (raw lead doc fields)
  { label: 'Hotel Name', value: '{{lead.hotel_name}}' },
  { label: 'Owner Email', value: '{{lead.email}}' },
  { label: 'Owner Phone', value: '{{lead.phone}}' },
  { label: 'City', value: '{{lead.city}}' },
  { label: 'State', value: '{{lead.state}}' },
  { label: 'Lead Stage', value: '{{lead.lead_stage}}' },
  { label: 'Lead Owner (Sales)', value: '{{lead.lead_owner}}' },
];



// Icon mapping
const ActionIcons = {
  send_email: Mail,
  send_sms: MessageSquare,
  send_whatsapp: MessageCircle,
  send_webhook: Globe,
  update_lead: Edit,
  create_task: CheckSquare,
  assign_lead: UserPlus,
  send_notification: Bell,
};

// Collapsible Card Component
const CollapsibleCard = ({ title, icon: Icon, children, defaultOpen = true, actions }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      <div 
        className="flex items-center justify-between px-4 py-3 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-2">
          {Icon && <Icon size={18} className="text-teal-600" />}
          <h3 className="font-semibold text-gray-800">{title}</h3>
        </div>
        <div className="flex items-center gap-2">
          {actions}
          {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </div>
      </div>
      {isOpen && <div className="p-4">{children}</div>}
    </div>
  );
};

// Condition Rule Component
const ConditionRule = ({ rule, index, onChange, onRemove, fields }) => {
  const selectedField = fields.find(f => f.field === rule.field);
  
  return (
    <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
      <GripVertical size={16} className="text-gray-400 cursor-move" />
      
      <select
        value={rule.field}
        onChange={(e) => onChange(index, 'field', e.target.value)}
        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none bg-white text-sm"
      >
        <option value="">Select Field</option>
        {fields.map(f => (
          <option key={f.field} value={f.field}>{f.label}</option>
        ))}
      </select>
      
      <select
        value={rule.operator}
        onChange={(e) => onChange(index, 'operator', e.target.value)}
        className="w-40 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none bg-white text-sm"
      >
        {CONDITION_OPERATORS.map(op => (
          <option key={op.id} value={op.id}>{op.name}</option>
        ))}
      </select>
      
      {selectedField?.type === 'select' ? (
        <select
          value={rule.value}
          onChange={(e) => onChange(index, 'value', e.target.value)}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none bg-white text-sm"
        >
          <option value="">Select Value</option>
          {selectedField.options.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      ) : (
        <input
          type={selectedField?.type === 'number' ? 'number' : 'text'}
          value={rule.value}
          onChange={(e) => onChange(index, 'value', e.target.value)}
          placeholder="Enter value"
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none text-sm"
        />
      )}
      
      <button
        onClick={() => onRemove(index)}
        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
      >
        <Trash2 size={16} />
      </button>
    </div>
  );
};

// Condition Group Component
const ConditionGroup = ({ group, onChange, depth = 0 }) => {
  const addRule = () => {
    const newRules = [...(group.rules || []), getEmptyConditionRule()];
    onChange({ ...group, rules: newRules });
  };
  
  const addGroup = () => {
    const newGroups = [...(group.groups || []), getEmptyConditionGroup()];
    onChange({ ...group, groups: newGroups });
  };
  
  const updateRule = (index, field, value) => {
    const newRules = [...group.rules];
    newRules[index] = { ...newRules[index], [field]: value };
    onChange({ ...group, rules: newRules });
  };
  
  const removeRule = (index) => {
    const newRules = group.rules.filter((_, i) => i !== index);
    onChange({ ...group, rules: newRules });
  };
  
  const updateNestedGroup = (index, newGroup) => {
    const newGroups = [...group.groups];
    newGroups[index] = newGroup;
    onChange({ ...group, groups: newGroups });
  };
  
  const removeNestedGroup = (index) => {
    const newGroups = group.groups.filter((_, i) => i !== index);
    onChange({ ...group, groups: newGroups });
  };
  
  return (
    <div className={`space-y-3 ${depth > 0 ? 'ml-6 pl-4 border-l-2 border-teal-200' : ''}`}>
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-gray-600">Match</span>
        <select
          value={group.logic}
          onChange={(e) => onChange({ ...group, logic: e.target.value })}
          className="px-3 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none bg-white text-sm font-medium"
        >
          <option value="and">ALL conditions (AND)</option>
          <option value="or">ANY condition (OR)</option>
        </select>
      </div>
      
      {group.rules?.map((rule, index) => (
        <ConditionRule
          key={index}
          rule={rule}
          index={index}
          onChange={updateRule}
          onRemove={removeRule}
          fields={CONDITION_FIELDS}
        />
      ))}
      
      {group.groups?.map((nestedGroup, index) => (
        <div key={index} className="relative">
          <button
            onClick={() => removeNestedGroup(index)}
            className="absolute -left-2 top-2 p-1 text-gray-400 hover:text-red-500 bg-white rounded-full border border-gray-200"
          >
            <X size={12} />
          </button>
          <ConditionGroup
            group={nestedGroup}
            onChange={(newGroup) => updateNestedGroup(index, newGroup)}
            depth={depth + 1}
          />
        </div>
      ))}
      
      <div className="flex items-center gap-2">
        <button
          onClick={addRule}
          className="flex items-center gap-1 px-3 py-1.5 text-sm text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
        >
          <Plus size={14} /> Add Condition
        </button>
        {depth < 2 && (
          <button
            onClick={addGroup}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Plus size={14} /> Add Group
          </button>
        )}
      </div>
    </div>
  );
};

// Custom Meta Template Dropdown
// ----------------------------------------------------------------------
// Why a custom component instead of <select><option>?
// The visual-editing tooling injects <span data-ve-dynamic> wrappers inside
// every <select> and <option> in this app. Browsers (and React's hydration)
// reject <span> as a valid <select>/<option> child, which (1) emits noisy
// console warnings, (2) breaks Playwright's select_option, and most
// importantly (3) intercepts click events on <option> rows so real users
// click an option but the underlying onChange never fires. A pure
// button-and-popover dropdown sidesteps the form-element interference
// entirely.
//
// Features:
//   - Click outside to close (mousedown listener on document)
//   - Keyboard: Enter/Space toggle, Esc closes, ArrowUp/Down navigate,
//     Enter selects highlighted row, Tab closes
//   - Search box filters by name / language / category
//   - High z-index (z-50) + relative anchor so the panel never gets
//     clipped by nearby cards
//   - Loading spinner state via `loading` prop
//   - data-testids on every interactive element
// ----------------------------------------------------------------------
const MetaTemplateDropdown = ({
  value,
  options,
  onSelect,
  loading = false,
  placeholder = 'Select a Meta template',
  testIdPrefix = 'meta-tpl',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const wrapperRef = useRef(null);
  const buttonRef = useRef(null);
  const inputRef = useRef(null);

  const selected = (options || []).find((t) => t.id === value) || null;

  // Filter list by search query across name / language / category / type / source
  const filtered = (options || []).filter((t) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    const srcLabel = t.source === 'meta' ? 'meta' : 'crm';
    return (
      (t.name || '').toLowerCase().includes(q) ||
      (t.language || '').toLowerCase().includes(q) ||
      (t.category || '').toLowerCase().includes(q) ||
      (t.type || '').toLowerCase().includes(q) ||
      srcLabel.includes(q)
    );
  });

  // Click-outside-to-close
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Focus search input when opened, clamp highlight when list changes
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 0);
      setHighlight(0);
    }
  }, [open]);

  useEffect(() => {
    if (highlight >= filtered.length) setHighlight(Math.max(0, filtered.length - 1));
  }, [filtered.length, highlight]);

  const choose = (tpl) => {
    // Debug log requested by the spec — verify selection event fires.
    // eslint-disable-next-line no-console
    console.log('[MetaTemplateDropdown] selected:', {
      id: tpl.id,
      name: tpl.name,
      language: tpl.language,
      category: tpl.category,
      status: tpl.status,
    });
    onSelect(tpl);
    setOpen(false);
    setQuery('');
    // Restore focus to the trigger button so keyboard flow continues naturally.
    setTimeout(() => buttonRef.current?.focus(), 0);
  };

  const onKeyDown = (e) => {
    if (!open) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === 'Escape' || e.key === 'Tab') {
      e.preventDefault();
      setOpen(false);
      buttonRef.current?.focus();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const tpl = filtered[highlight];
      if (tpl) choose(tpl);
    }
  };

  return (
    <div className="relative" ref={wrapperRef} onKeyDown={onKeyDown}>
      <button
        type="button"
        ref={buttonRef}
        onClick={() => setOpen((o) => !o)}
        disabled={loading}
        data-testid={`${testIdPrefix}-trigger`}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 border border-gray-300 rounded-lg bg-white text-left focus:outline-none focus:ring-2 focus:ring-teal-500 hover:border-gray-400 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        <span className="flex-1 truncate text-sm text-gray-800">
          {loading ? (
            <span className="inline-flex items-center gap-2 text-gray-500">
              <Loader2 size={14} className="animate-spin" /> Loading templates…
            </span>
          ) : selected ? (
            <>
              <span className="font-medium">{selected.name}</span>
              <span className="text-gray-500"> · {selected.source === 'meta' ? 'Meta' : 'CRM'}</span>
            </>
          ) : (
            <span className="text-gray-400">
              {(options || []).length
                ? `${placeholder} (${(options || []).length})`
                : placeholder}
            </span>
          )}
        </span>
        <ChevronDown size={16} className={`text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          className="absolute left-0 right-0 mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
          role="listbox"
          data-testid={`${testIdPrefix}-panel`}
        >
          <div className="flex items-center gap-2 px-2 py-2 border-b border-gray-100 bg-gray-50">
            <Search size={14} className="text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name / type / source (Meta / CRM)…"
              data-testid={`${testIdPrefix}-search`}
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-gray-400"
            />
          </div>
          <ul className="max-h-72 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-xs text-gray-500 italic">
                No templates match "{query}".
              </li>
            ) : (
              filtered.map((t, i) => {
                const prev = filtered[i - 1];
                const showHeader = !prev || (prev.source === 'meta') !== (t.source === 'meta');
                return (
                <React.Fragment key={t.id}>
                  {showHeader && (
                    <li className="px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-gray-400 select-none"
                      data-testid={`${testIdPrefix}-group-${t.source === 'meta' ? 'meta' : 'crm'}`}
                    >
                      {t.source === 'meta' ? 'Meta Templates' : 'CRM Templates'}
                    </li>
                  )}
                  <li
                  role="option"
                  aria-selected={t.id === value}
                  data-testid={`${testIdPrefix}-option-${t.name}-${t.source}`}
                  onMouseEnter={() => setHighlight(i)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    choose(t);
                  }}
                  className={`flex items-center justify-between gap-2 px-3 py-2 text-sm cursor-pointer ${
                    i === highlight ? 'bg-teal-50' : 'hover:bg-gray-50'
                  } ${t.id === value ? 'text-teal-700 font-medium' : 'text-gray-700'}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate">
                      <span className="font-medium">{t.name}</span>
                      {t.language && t.language !== '—' && (
                        <span className="text-gray-500"> · {t.language}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 mt-0.5">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${
                        t.source === 'meta' ? 'bg-blue-100 text-blue-700' : 'bg-teal-100 text-teal-700'
                      }`}>
                        {t.source === 'meta' ? 'Meta' : 'CRM'}
                      </span>
                      {t.status && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
                          {t.status}
                        </span>
                      )}
                      {t.category && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                          {t.category}
                        </span>
                      )}
                    </div>
                  </div>
                  {t.id === value && <Check size={16} className="text-teal-600 shrink-0" />}
                  </li>
                </React.Fragment>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

// Action Card Component
const ActionCard = ({ action, index, onChange, onRemove, templates, metaTemplates, onSyncMetaTemplates, metaSyncing, metaSyncState }) => {
  const actionType = ACTION_TYPES.find(a => a.id === action.type);
  const ActionIcon = ActionIcons[action.type] || Zap;
  
  const updateConfig = (key, value) => {
    onChange(index, 'config', { ...action.config, [key]: value });
  };

  // WhatsApp Action dropdown = Meta-approved templates + CRM manual templates
  // (type whatsapp OR both). Each option carries a `source` ('meta'|'crm') so
  // the dropdown can badge + group them. CRM 'both' templates are also offered
  // in the Email action (handled in the Email select above).
  const crmWhatsappTemplates = (templates || [])
    .filter(t => t.source !== 'meta' && (t.type === 'whatsapp' || t.type === 'both'))
    .map(t => ({
      ...t,
      source: 'crm',
      language: t.language || '—',
      category: t.category || (t.type === 'both' ? 'Both' : 'WhatsApp'),
      status: t.is_active === false ? 'Inactive' : 'CRM',
    }));
  const whatsappOptions = [
    ...(metaTemplates || []).map(t => ({ ...t, source: 'meta' })),
    ...crmWhatsappTemplates,
  ];

  // Currently selected WhatsApp template (Meta OR CRM) for the details panel.
  const selectedMetaTemplate = action.type === 'send_whatsapp' && action.template_id
    ? whatsappOptions.find(t => t.id === action.template_id)
    : null;

  // When a template is selected we auto-store its metadata in config so the
  // workflow_engine can read it at execution time. Meta templates also store
  // positional variable bindings; CRM templates resolve named {{lead.x}}
  // variables from context at runtime so no manual mapping is needed.
  const handleMetaTemplateChange = (tpl) => {
    if (!tpl) {
      onChange(index, 'template_id', '');
      return;
    }
    onChange(index, 'template_id', tpl.id);
    if (tpl.source === 'crm') {
      onChange(index, 'config', {
        ...action.config,
        template_name: tpl.name,
        template_source: 'crm',
        template_variables: {},
      });
      return;
    }
    const vars = {};
    (tpl.variables || []).forEach((v) => {
      vars[v] = action.config?.template_variables?.[v] || '';
    });
    onChange(index, 'config', {
      ...action.config,
      template_name: tpl.name,
      template_language: tpl.language,
      template_category: tpl.category,
      template_source: 'meta',
      template_variables: vars,
    });
  };

  const updateTemplateVariable = (key, value) => {
    onChange(index, 'config', {
      ...action.config,
      template_variables: { ...(action.config?.template_variables || {}), [key]: value },
    });
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div className={`flex items-center justify-between px-4 py-3 ${actionType?.color || 'bg-gray-500'} text-white`}>
        <div className="flex items-center gap-2">
          <ActionIcon size={18} />
          <span className="font-medium">{actionType?.name || action.type}</span>
        </div>
        <button
          onClick={() => onRemove(index)}
          className="p-1 hover:bg-white/20 rounded transition-colors"
          data-testid={`workflow-action-remove-${index}`}
        >
          <Trash2 size={16} />
        </button>
      </div>
      
      <div className="p-4 space-y-4">
        {/* WhatsApp — Meta-approved Cloud API templates ONLY */}
        {action.type === 'send_whatsapp' && (
          <div data-testid={`workflow-action-whatsapp-${index}`}>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700">
                WhatsApp Template <span className="text-gray-400 font-normal">(Meta &amp; CRM)</span>
              </label>
              <div className="flex items-center gap-2">
                {metaSyncState?.lastSyncedAt && (
                  <span className="text-xs text-gray-500">
                    Last sync: {new Date(metaSyncState.lastSyncedAt).toLocaleTimeString()}
                  </span>
                )}
                <button
                  type="button"
                  onClick={onSyncMetaTemplates}
                  disabled={metaSyncing}
                  data-testid="workflow-sync-meta-templates-btn"
                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 rounded transition-colors disabled:opacity-60"
                >
                  <RefreshCw size={12} className={metaSyncing ? 'animate-spin' : ''} />
                  {metaSyncing ? 'Syncing…' : 'Sync Meta Templates'}
                </button>
              </div>
            </div>
            {/* Phase 16.2 — Custom button+popover dropdown replaces the
                native <select>. Reasons:
                  - ve-tooling injects <span data-ve-dynamic> wrappers inside
                    every <select>/<option>, which intercepts native clicks
                    and breaks React's synthetic onChange propagation.
                  - The custom widget already provides full a11y via
                    aria-haspopup/aria-expanded on the trigger button and
                    role=listbox/role=option on the panel. No hidden form
                    element needed. */}
            <MetaTemplateDropdown
              value={action.template_id || ''}
              options={whatsappOptions}
              onSelect={handleMetaTemplateChange}
              loading={metaSyncing}
              placeholder="Select a WhatsApp template (Meta or CRM)"
              testIdPrefix={`workflow-meta-template-${index}`}
            />
            {/* Hidden input mirrors template_id for legacy data-testid
                consumers (e.g. tests that previously queried
                `workflow-meta-template-select-{index}`). NOT a <select>,
                so no ve-tooling hydration warning. */}
            <input
              type="hidden"
              data-testid={`workflow-meta-template-select-${index}`}
              value={action.template_id || ''}
              readOnly
            />

            {/* Selected template metadata + variables (Meta only) */}
            {selectedMetaTemplate && selectedMetaTemplate.source === 'meta' && (
              <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg space-y-3"
                data-testid={`workflow-meta-template-details-${index}`}
              >
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  {/* "Meta Synced" badge — confirms the template body is the
                      live Meta-approved content and is NOT editable here. */}
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-semibold uppercase tracking-wide"
                    data-testid={`workflow-meta-synced-badge-${index}`}
                    title="Body is read-only and pulled directly from Meta. Edit the template in Meta Business Manager."
                  >
                    <RefreshCw size={11} /> Meta Synced
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">
                    <CheckCircle size={11} /> {selectedMetaTemplate.status}
                  </span>
                  <span className="px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-medium">
                    {selectedMetaTemplate.language}
                  </span>
                  <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
                    {selectedMetaTemplate.category}
                  </span>
                </div>

                {(selectedMetaTemplate.variables || []).length > 0 ? (
                  <div className="space-y-2">
                    <div className="text-xs font-medium text-gray-700">
                      Map values for each Meta placeholder (the body itself is locked):
                    </div>
                    {(selectedMetaTemplate.variables || []).map((v) => {
                      const currentVal = action.config?.template_variables?.[v] || '';
                      // Detect if current value matches a CRM-field token so
                      // the dropdown reflects the active mapping.
                      const matchedOpt = CRM_FIELD_OPTIONS.find((o) => o.value === currentVal);
                      return (
                        <div key={v} className="flex items-center gap-2">
                          <span className="text-xs font-mono text-gray-600 w-12">{`{{${v}}}`}</span>
                          <input
                            type="text"
                            value={currentVal}
                            onChange={(e) => updateTemplateVariable(v, e.target.value)}
                            placeholder="Pick a CRM field → or type a static value"
                            data-testid={`workflow-template-var-${index}-${v}`}
                            className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-teal-500 outline-none font-mono"
                          />
                          {/* CRM-field dropdown — pastes {{lead.X}} into the
                              input on selection. Replaces static sample values
                              like 'Hotel Sunshine' with live lead data at
                              workflow runtime. */}
                          <select
                            value={matchedOpt ? matchedOpt.value : ''}
                            onChange={(e) => {
                              if (e.target.value) {
                                updateTemplateVariable(v, e.target.value);
                              }
                            }}
                            data-testid={`workflow-template-var-${index}-${v}-crm-select`}
                            title="Insert a dynamic CRM field"
                            className="px-2 py-1 text-xs border border-gray-300 rounded bg-white text-gray-600 focus:ring-1 focus:ring-teal-500 outline-none cursor-pointer"
                          >
                            <option value="">CRM field…</option>
                            {CRM_FIELD_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      );
                    })}
                    <div className="text-[11px] text-gray-500 italic mt-1">
                      Tip: pick a CRM field from the dropdown to bind a Meta
                      placeholder to live lead data. Empty values render as
                      "N/A" so the message always delivers.
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-gray-500 italic">
                    This template has no variables — it will be sent as-is.
                  </div>
                )}

                {/* LIVE PREVIEW — Meta-approved body with the user's mapped
                    values substituted in real-time. This is read-only proof
                    that the EXACT Meta-approved content (with current variable
                    bindings) will be dispatched. The user CANNOT edit the
                    body — edits must happen in Meta Business Manager. */}
                {(selectedMetaTemplate.components || [])
                  .filter((c) => ['HEADER', 'BODY', 'FOOTER'].includes((c.type || '').toUpperCase()))
                  .map((c, ci) => {
                    let rendered = c.text || '';
                    Object.entries(action.config?.template_variables || {}).forEach(([k, val]) => {
                      const safe = val && String(val).trim() ? String(val) : `〈${k}〉`;
                      rendered = rendered.split(`{{${k}}}`).join(safe);
                    });
                    const label = (c.type || '').toUpperCase();
                    return (
                      <div
                        key={ci}
                        className="text-xs text-gray-700 whitespace-pre-wrap bg-white border border-gray-200 rounded p-2"
                        data-testid={`workflow-meta-template-preview-${label.toLowerCase()}-${index}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-semibold text-gray-500 uppercase text-[10px] tracking-wider">{label} (read-only)</span>
                          <span className="text-[10px] text-gray-400">Synced from Meta</span>
                        </div>
                        {rendered}
                      </div>
                    );
                  })}

                <div className="text-[11px] text-gray-500 italic flex items-start gap-1">
                  <AlertCircle size={11} className="shrink-0 mt-0.5" />
                  The Meta-approved body is locked. To change the wording, edit the template
                  in Meta Business Manager and click "Sync Meta Templates".
                </div>
              </div>
            )}

            {/* Selected template details — CRM manual template */}
            {selectedMetaTemplate && selectedMetaTemplate.source === 'crm' && (
              <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg space-y-2"
                data-testid={`workflow-crm-template-details-${index}`}
              >
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-teal-100 text-teal-700 font-semibold uppercase tracking-wide">
                    <Edit size={11} /> CRM Template
                  </span>
                  <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
                    {selectedMetaTemplate.type === 'both' ? 'Both (Email + WhatsApp)' : 'WhatsApp'}
                  </span>
                </div>
                <div className="text-xs text-gray-700 whitespace-pre-wrap bg-white border border-gray-200 rounded p-2 max-h-40 overflow-y-auto"
                  data-testid={`workflow-crm-template-preview-${index}`}
                >
                  {selectedMetaTemplate.body || '(no body)'}
                </div>
                <div className="text-[11px] text-gray-500 italic flex items-start gap-1">
                  <AlertCircle size={11} className="shrink-0 mt-0.5" />
                  Variables like {'{{lead.hotelName}}'} resolve automatically from the lead at send time. Edit the body in the Templates tab. Sent as plain WhatsApp text (HTML is stripped).
                </div>
              </div>
            )}

            {metaSyncState?.lastError && (
              <div className="mt-2 flex items-start gap-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">
                <AlertCircle size={14} className="shrink-0 mt-0.5" />
                <span>Last sync failed: {metaSyncState.lastError}</span>
              </div>
            )}
          </div>
        )}

        {/* Email / SMS — internal CRM templates (Email also shows type=Both) */}
        {['send_email', 'send_sms'].includes(action.type) && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Message Template</label>
            <select
              value={action.template_id || ''}
              onChange={(e) => onChange(index, 'template_id', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none bg-white"
              data-testid={`workflow-email-template-select-${index}`}
            >
              <option value="">Select Template</option>
              {templates
                .filter(t => {
                  if (t.source === 'meta') return false;  // Meta = WhatsApp only
                  if (action.type === 'send_email') return t.type === 'email' || t.type === 'both';
                  return t.type === action.type.replace('send_', '');
                })
                .map(t => (
                  <option key={t.id} value={t.id}>
                    {t.name} — {t.type === 'both' ? 'Both' : (t.type === 'email' ? 'Email' : 'SMS')} (CRM)
                  </option>
                ))}
            </select>
          </div>
        )}
        
        {/* Webhook URL */}
        {action.type === 'send_webhook' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Webhook URL</label>
            <input
              type="url"
              value={action.config?.webhook_url || ''}
              onChange={(e) => updateConfig('webhook_url', e.target.value)}
              placeholder="https://example.com/webhook"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
            />
          </div>
        )}
        
        {/* Task Details */}
        {action.type === 'create_task' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Task Title</label>
              <input
                type="text"
                value={action.config?.task_title || ''}
                onChange={(e) => updateConfig('task_title', e.target.value)}
                placeholder="Follow up with {{lead.hotelName}}"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Due in (days)</label>
              <input
                type="number"
                value={action.config?.due_days || 1}
                onChange={(e) => updateConfig('due_days', parseInt(e.target.value))}
                min="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
              />
            </div>
          </>
        )}
        
        {/* Assign Lead */}
        {action.type === 'assign_lead' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Assign To</label>
            <input
              type="text"
              value={action.config?.assign_to || ''}
              onChange={(e) => updateConfig('assign_to', e.target.value)}
              placeholder="User name or email"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
            />
          </div>
        )}
        
        {/* Delay */}
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Delay</label>
            <div className="flex gap-2">
              <input
                type="number"
                value={action.delay_value ?? action.delay_minutes ?? 0}
                onChange={(e) => {
                  const v = parseFloat(e.target.value) || 0;
                  onChange(index, 'delay_value', v);
                  if (!action.delay_unit) onChange(index, 'delay_unit', 'minutes');
                }}
                min="0"
                className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                data-testid={`action-delay-value-${index}`}
              />
              <select
                value={action.delay_unit || 'minutes'}
                onChange={(e) => {
                  onChange(index, 'delay_unit', e.target.value);
                  if (action.delay_value === undefined || action.delay_value === null) {
                    onChange(index, 'delay_value', action.delay_minutes || 0);
                  }
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none bg-white text-sm"
                data-testid={`action-delay-unit-${index}`}
              >
                <option value="seconds">Seconds</option>
                <option value="minutes">Minutes</option>
                <option value="hours">Hours</option>
              </select>
            </div>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
            <select
              value={action.priority || 1}
              onChange={(e) => onChange(index, 'priority', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none bg-white"
            >
              <option value={1}>High</option>
              <option value={2}>Medium</option>
              <option value={3}>Low</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

// Template Editor Modal
const TemplateEditorModal = ({ isOpen, onClose, onSave, template, type }) => {
  const [formData, setFormData] = useState({
    name: '',
    type: type || 'email',
    subject: '',
    body: '',
    variables: []
  });
  
  useEffect(() => {
    if (template) {
      setFormData(template);
    } else {
      setFormData({
        name: '',
        type: type || 'email',
        subject: '',
        body: '',
        variables: []
      });
    }
  }, [template, type, isOpen]);
  
  if (!isOpen) return null;
  
  const insertVariable = (varName) => {
    const textarea = document.getElementById('template-body');
    const cursorPos = textarea?.selectionStart || formData.body.length;
    const newBody = formData.body.slice(0, cursorPos) + `{{${varName}}}` + formData.body.slice(cursorPos);
    setFormData(prev => ({ ...prev, body: newBody }));
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50">
          <h2 className="text-xl font-semibold text-gray-800">
            {template ? 'Edit Template' : 'Create Template'}
          </h2>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-full">
            <X size={20} />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-3 gap-6">
            <div className="col-span-2 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Template Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                  placeholder="e.g., OTA Completion Notification"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none bg-white"
                >
                  <option value="email">Email</option>
                  <option value="sms">SMS</option>
                </select>
                {/* Phase 16 — WhatsApp template type intentionally removed.
                    WhatsApp workflow templates MUST be created/approved in
                    Meta Business Manager and synced via the "Sync Meta
                    Templates" button on the workflow action card. */}
                <p className="mt-1 text-[11px] text-gray-500">
                  WhatsApp templates are managed in Meta Business Manager and
                  auto-synced. Use the "Sync Meta Templates" button on the
                  Send WhatsApp action.
                </p>
              </div>
              
              {formData.type === 'email' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                  <input
                    type="text"
                    value={formData.subject}
                    onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                    placeholder="e.g., Your OTA listing is now live!"
                  />
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message Body</label>
                <textarea
                  id="template-body"
                  value={formData.body}
                  onChange={(e) => setFormData(prev => ({ ...prev, body: e.target.value }))}
                  rows={10}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none font-mono text-sm"
                  placeholder="Hello {{lead.ownerName}},&#10;&#10;Your OTA listing for {{lead.hotelName}} on {{ota.otaName}} is now complete!"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Available Variables</label>
              <div className="bg-gray-50 rounded-lg p-3 max-h-96 overflow-y-auto">
                {Object.entries(
                  TEMPLATE_VARIABLES.reduce((acc, v) => {
                    if (!acc[v.category]) acc[v.category] = [];
                    acc[v.category].push(v);
                    return acc;
                  }, {})
                ).map(([category, vars]) => (
                  <div key={category} className="mb-3">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">{category}</p>
                    {vars.map(v => (
                      <button
                        key={v.name}
                        onClick={() => insertVariable(v.name)}
                        className="block w-full text-left px-2 py-1 text-sm text-teal-600 hover:bg-teal-50 rounded transition-colors"
                      >
                        {`{{${v.name}}}`}
                        <span className="text-gray-400 text-xs ml-1">- {v.description}</span>
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg font-medium">
            Cancel
          </button>
          <button
            onClick={() => onSave(formData)}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium flex items-center"
          >
            <Save size={18} className="mr-2" /> Save Template
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Workflow Builder Component
const WorkflowBuilder = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);
  
  const [workflow, setWorkflow] = useState(getEmptyWorkflow());
  const [templates, setTemplates] = useState([]);
  const [metaTemplates, setMetaTemplates] = useState([]);
  const [metaSyncing, setMetaSyncing] = useState(false);
  const [metaSyncState, setMetaSyncState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [templateModal, setTemplateModal] = useState({ open: false, template: null, type: null });
  
  useEffect(() => {
    fetchTemplates();
    fetchMetaTemplates();
    if (isEditing) {
      fetchWorkflow();
    }
  }, [id]);

  // Auto-refresh Meta templates every 10 minutes while the builder is open.
  // Backend also runs a 10-min background sync — this keeps the UI in sync
  // even if the user keeps the page open for hours.
  useEffect(() => {
    const interval = setInterval(() => {
      fetchMetaTemplates();
    }, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);
  
  const fetchWorkflow = async () => {
    try {
      setLoading(true);
      const response = await workflowsV2API.getById(id);
      setWorkflow(response.data);
    } catch (error) {
      console.error('Error fetching workflow:', error);
      alert('Failed to load workflow');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchTemplates = async () => {
    try {
      const response = await templatesAPI.getAll();
      setTemplates(response.data.data || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchMetaTemplates = async (force = false) => {
    try {
      const response = await whatsappAPI.getMetaTemplates({ status: 'APPROVED', force });
      setMetaTemplates(response.data.data || []);
      setMetaSyncState(response.data.syncState || null);
    } catch (error) {
      console.error('Error fetching Meta templates:', error);
    }
  };

  const handleSyncMetaTemplates = async () => {
    try {
      setMetaSyncing(true);
      const response = await whatsappAPI.syncMetaTemplates();
      if (response.data?.ok === false) {
        alert(`Meta template sync failed: ${response.data.error || 'Unknown error'}`);
      }
      await fetchMetaTemplates();
    } catch (error) {
      console.error('Error syncing Meta templates:', error);
      alert(`Sync failed: ${error.response?.data?.detail || error.message}`);
    } finally {
      setMetaSyncing(false);
    }
  };
  
  const handleSave = async (status = workflow.status) => {
    if (!workflow.name.trim()) {
      alert('Please enter a workflow name');
      return;
    }
    if (!workflow.trigger_event) {
      alert('Please select a trigger event');
      return;
    }
    
    try {
      setSaving(true);
      const data = { ...workflow, status };
      
      if (isEditing) {
        await workflowsV2API.update(id, data);
      } else {
        await workflowsV2API.create(data);
      }
      
      navigate('/workflows');
    } catch (error) {
      console.error('Error saving workflow:', error);
      alert('Failed to save workflow');
    } finally {
      setSaving(false);
    }
  };
  
  const handleTest = async () => {
    if (!isEditing) {
      alert('Please save the workflow first before testing');
      return;
    }
    
    try {
      setTesting(true);
      const result = await workflowsV2API.run(id, { lead_id: 'test' });
      alert(`Workflow test completed!\nStatus: ${result.data.status}\nActions executed: ${result.data.actions_executed?.length || 0}`);
    } catch (error) {
      console.error('Error testing workflow:', error);
      const detail = error.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail : (detail?.message || detail?.msg || error.message || 'Unknown error');
      alert('Workflow test failed: ' + errorMsg);
    } finally {
      setTesting(false);
    }
  };
  
  const addAction = (type) => {
    setWorkflow(prev => ({
      ...prev,
      actions: [...prev.actions, getEmptyAction(type)]
    }));
  };
  
  const updateAction = (index, field, value) => {
    // Functional setState — critical for callers that make multiple
    // updateAction() calls in a row (e.g. the Meta template picker which
    // updates `template_id` AND `config` back-to-back). Reading
    // `workflow.actions` from the closure would silently lose the first
    // update because both callbacks read the same stale snapshot.
    setWorkflow((prev) => {
      const newActions = [...prev.actions];
      newActions[index] = { ...newActions[index], [field]: value };
      return { ...prev, actions: newActions };
    });
  };
  
  const removeAction = (index) => {
    setWorkflow(prev => ({
      ...prev,
      actions: prev.actions.filter((_, i) => i !== index)
    }));
  };
  
  const handleSaveTemplate = async (templateData) => {
    try {
      if (templateData.id) {
        await templatesAPI.update(templateData.id, templateData);
      } else {
        await templatesAPI.create(templateData);
      }
      fetchTemplates();
      setTemplateModal({ open: false, template: null, type: null });
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Failed to save template');
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-100px)]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }
  
  const selectedTrigger = TRIGGER_EVENTS.find(t => t.id === workflow.trigger_event);
  
  return (
    <div className="space-y-6" data-testid="workflow-builder">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/workflows')}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              {isEditing ? 'Edit Workflow' : 'Create Workflow'}
            </h1>
            <p className="text-sm text-gray-500">Build automated actions based on triggers and conditions</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={() => handleSave('draft')}
            disabled={saving}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            Save as Draft
          </button>
          {isEditing && (
            <button
              onClick={handleTest}
              disabled={testing}
              className="px-4 py-2 border border-teal-300 text-teal-600 rounded-lg hover:bg-teal-50 transition-colors font-medium flex items-center"
            >
              <Play size={18} className="mr-2" />
              {testing ? 'Testing...' : 'Test'}
            </button>
          )}
          <button
            onClick={() => handleSave('active')}
            disabled={saving}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors shadow-sm font-medium flex items-center"
          >
            <Save size={18} className="mr-2" />
            {saving ? 'Saving...' : 'Save & Activate'}
          </button>
        </div>
      </div>
      
      {/* Workflow Name */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Workflow Name</label>
        <input
          type="text"
          value={workflow.name}
          onChange={(e) => setWorkflow(prev => ({ ...prev, name: e.target.value }))}
          placeholder="e.g., Send OTA Completion Notification"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none text-lg"
        />
        <input
          type="text"
          value={workflow.description}
          onChange={(e) => setWorkflow(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Add a description (optional)"
          className="w-full px-4 py-2 mt-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none text-sm text-gray-600"
        />
      </div>
      
      {/* Trigger Event */}
      <CollapsibleCard title="Trigger Event" icon={Zap}>
        <div className="space-y-4">
          <p className="text-sm text-gray-600">Select when this workflow should be triggered</p>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {TRIGGER_EVENTS.map(trigger => (
              <button
                key={trigger.id}
                onClick={() => setWorkflow(prev => ({ ...prev, trigger_event: trigger.id }))}
                className={`p-3 rounded-lg border-2 text-left transition-all ${
                  workflow.trigger_event === trigger.id
                    ? 'border-teal-500 bg-teal-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <p className="font-medium text-gray-800">{trigger.name}</p>
                <p className="text-xs text-gray-500 mt-1">{trigger.category}</p>
              </button>
            ))}
          </div>
          
          {selectedTrigger && (
            <div className="p-3 bg-teal-50 rounded-lg border border-teal-100">
              <p className="text-sm text-teal-800">{selectedTrigger.description}</p>
            </div>
          )}
        </div>
      </CollapsibleCard>
      
      {/* Conditions */}
      <CollapsibleCard title="Conditions (Optional)" icon={Settings}>
        <div className="space-y-4">
          <p className="text-sm text-gray-600">Add conditions to filter when this workflow should run</p>
          
          <ConditionGroup
            group={workflow.conditions}
            onChange={(newConditions) => setWorkflow(prev => ({ ...prev, conditions: newConditions }))}
          />
        </div>
      </CollapsibleCard>
      
      {/* Actions */}
      <CollapsibleCard 
        title="Actions" 
        icon={Zap}
        actions={
          <button
            className="px-3 py-1 text-sm text-teal-600 hover:bg-teal-50 rounded-lg transition-colors flex items-center gap-1"
            onClick={(e) => { e.stopPropagation(); setTemplateModal({ open: true, template: null, type: null }); }}
          >
            <Plus size={14} /> New Template
          </button>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">Define what should happen when conditions are met</p>
          
          {/* Action List */}
          <div className="space-y-4">
            {workflow.actions.map((action, index) => (
              <ActionCard
                key={index}
                action={action}
                index={index}
                onChange={updateAction}
                onRemove={removeAction}
                templates={templates}
                metaTemplates={metaTemplates}
                onSyncMetaTemplates={handleSyncMetaTemplates}
                metaSyncing={metaSyncing}
                metaSyncState={metaSyncState}
              />
            ))}
          </div>
          
          {/* Add Action Buttons */}
          <div className="flex flex-wrap gap-2 pt-2">
            {ACTION_TYPES.map(actionType => {
              const Icon = ActionIcons[actionType.id] || Zap;
              return (
                <button
                  key={actionType.id}
                  onClick={() => addAction(actionType.id)}
                  className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                >
                  <Icon size={16} className="text-gray-500" />
                  {actionType.name}
                </button>
              );
            })}
          </div>
        </div>
      </CollapsibleCard>
      
      {/* Schedule Settings */}
      <CollapsibleCard title="Schedule Settings" icon={Settings} defaultOpen={false}>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">When to run</label>
            <div className="flex gap-3">
              {SCHEDULE_TYPES.map(scheduleType => (
                <button
                  key={scheduleType.id}
                  onClick={() => setWorkflow(prev => ({
                    ...prev,
                    schedule: { ...prev.schedule, type: scheduleType.id }
                  }))}
                  className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                    workflow.schedule?.type === scheduleType.id
                      ? 'border-teal-500 bg-teal-50 text-teal-700'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  {scheduleType.name}
                </button>
              ))}
            </div>
          </div>
          
          {workflow.schedule?.type === 'delayed' && (
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-gray-700">Delay:</label>
              <input
                type="number"
                data-testid="schedule-delay-minutes-input"
                value={workflow.schedule?.delay_minutes || 0}
                onChange={(e) => setWorkflow(prev => ({
                  ...prev,
                  schedule: { ...prev.schedule, delay_minutes: parseInt(e.target.value) || 0 }
                }))}
                min="0"
                className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
              />
              <span className="text-sm text-gray-500">minutes</span>
            </div>
          )}
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date (Optional)</label>
              <input
                type="date"
                value={workflow.schedule?.start_date || ''}
                onChange={(e) => setWorkflow(prev => ({
                  ...prev,
                  schedule: { ...prev.schedule, start_date: e.target.value }
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date (Optional)</label>
              <input
                type="date"
                value={workflow.schedule?.end_date || ''}
                onChange={(e) => setWorkflow(prev => ({
                  ...prev,
                  schedule: { ...prev.schedule, end_date: e.target.value }
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
              />
            </div>
          </div>

          {(workflow.schedule?.type === 'scheduled' || workflow.schedule?.type === 'recurring') && (
            <div className="p-4 rounded-lg border border-teal-100 bg-teal-50/40 space-y-4" data-testid="schedule-time-block">
              {workflow.schedule?.type === 'recurring' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Repeat</label>
                  <div className="flex flex-wrap gap-2">
                    {RECURRING_FREQUENCIES.map(freq => (
                      <button
                        key={freq.id}
                        type="button"
                        data-testid={`schedule-frequency-${freq.id}`}
                        onClick={() => setWorkflow(prev => ({
                          ...prev,
                          schedule: { ...prev.schedule, frequency: freq.id }
                        }))}
                        title={freq.description}
                        className={`px-3 py-1.5 rounded-lg border-2 text-sm font-medium transition-colors ${
                          (workflow.schedule?.frequency || 'daily') === freq.id
                            ? 'border-teal-500 bg-teal-50 text-teal-700'
                            : 'border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        {freq.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Run at (time)
                  </label>
                  <input
                    type="time"
                    data-testid="schedule-time-input"
                    value={workflow.schedule?.time || ''}
                    onChange={(e) => setWorkflow(prev => ({
                      ...prev,
                      schedule: { ...prev.schedule, time: e.target.value || null }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">HH:MM (AM/PM). Required for this workflow to run on schedule.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
                  <select
                    data-testid="schedule-timezone-select"
                    value={workflow.schedule?.timezone || 'UTC'}
                    onChange={(e) => setWorkflow(prev => ({
                      ...prev,
                      schedule: { ...prev.schedule, timezone: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none bg-white"
                  >
                    <option value="UTC">UTC</option>
                    <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                    <option value="America/New_York">America/New_York</option>
                    <option value="Europe/London">Europe/London</option>
                    <option value="Asia/Dubai">Asia/Dubai</option>
                  </select>
                </div>
              </div>
              {!workflow.schedule?.time && (
                <p className="text-xs text-amber-600" data-testid="schedule-time-warning">
                  ⚠ Select a time, otherwise this workflow will not run automatically (legacy date-only behaviour).
                </p>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Run Limit (Optional)</label>
            <input
              type="number"
              value={workflow.schedule?.run_limit || ''}
              onChange={(e) => setWorkflow(prev => ({
                ...prev,
                schedule: { ...prev.schedule, run_limit: parseInt(e.target.value) || null }
              }))}
              min="1"
              placeholder="Unlimited"
              className="w-48 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
            />
            <p className="text-xs text-gray-500 mt-1">Maximum number of times this workflow can run</p>
          </div>
        </div>
      </CollapsibleCard>
      
      {/* Template Editor Modal */}
      <TemplateEditorModal
        isOpen={templateModal.open}
        onClose={() => setTemplateModal({ open: false, template: null, type: null })}
        onSave={handleSaveTemplate}
        template={templateModal.template}
        type={templateModal.type}
      />
    </div>
  );
};

export default WorkflowBuilder;
